// API

import { signal } from '@angular/core';
import {
  IWHBomNodeDTO,
  WHIconENUM,
  WHNestedNodeDOM,
} from '@workheld/workheld-shared-lib';

export function searchTree(element: WHNestedBomNodeDOM, nodeId: string) {
  if (element.id == nodeId) {
    return element;
  } else if (element.children != null) {
    let i: number;
    let result = null;
    for (i = 0; result == null && i < element.children.length; i++) {
      result = this.searchTree(element.children[i], nodeId);
    }
    return result;
  }
  return null;
}

/**
 * Builds the node tree for `rootId` out of a flat node list.
 *
 * Anchored on the root instead of on "the node that has no parent"
 * (WHP-12588): the root is returned even when its own level came back empty or
 * when it carries a `parentNodeId` of its own, and a node whose parent is
 * missing from `list` is hung on the root rather than dropped together with its
 * whole subtree.
 */
export function buildBomNodeTree(
  list: WHNestedBomNodeDOM[],
  rootId: string,
): WHNestedBomNodeDOM | null {
  const nodesById = new Map<string, WHNestedBomNodeDOM>();
  list.forEach((node) => {
    node.children = [];
    nodesById.set(node.id, node);
  });

  const root = nodesById.get(rootId);
  if (!root) {
    return null;
  }

  list.forEach((node) => {
    if (node === root) {
      return;
    }
    (nodesById.get(node.parentNodeId) ?? root).children.push(node);
  });

  return root;
}

/** One piece of a node name, split around a search term. */
export interface WHNodeNameSegment {
  text: string;
  isMatch: boolean;
}

/**
 * Splits `name` around every (case-insensitive) occurrence of `term`, so the
 * template can marker-highlight the matching pieces without going through
 * `innerHTML`.
 */
export function splitNameBySearchTerm(
  name: string | null | undefined,
  term: string | null | undefined,
): WHNodeNameSegment[] {
  if (!name || !term) {
    return [{ text: name ?? '', isMatch: false }];
  }

  const segments: WHNodeNameSegment[] = [];
  const haystack = name.toLowerCase();
  const needle = term.toLowerCase();
  let cursor = 0;
  let index = haystack.indexOf(needle);

  while (index !== -1) {
    if (index > cursor) {
      segments.push({ text: name.slice(cursor, index), isMatch: false });
    }
    segments.push({
      text: name.slice(index, index + needle.length),
      isMatch: true,
    });
    cursor = index + needle.length;
    index = haystack.indexOf(needle, cursor);
  }

  if (cursor < name.length) {
    segments.push({ text: name.slice(cursor), isMatch: false });
  }

  return segments;
}

export class WHNestedBomNodeDOM extends WHNestedNodeDOM {
  // DOM
  children: WHNestedNodeDOM[] = [];
  icon?: string = WHIconENUM.BomIcon;
  isLoading? = signal(false);
  /**
   * Depth of the node in the rendered tree, `0` for a node rendered at the top
   * level. Set together with the guide-line fields below when a level is built.
   */
  treeLevel?: number;
  /**
   * One flag per ancestor level between the top level and this node's own
   * indent step: `true` where that ancestor still has a following sibling, so
   * its vertical guide line continues past this row. Shared between siblings —
   * treat it as read-only.
   */
  guides?: boolean[];
  /** `true` when this node is the last of its rendered siblings (draws `└`). */
  isLastChild?: boolean;
  /**
   * `name` pre-split around the active search term (WHP-12435). Only set on
   * search result nodes; the browse tree renders `name` directly.
   */
  nameSegments?: WHNodeNameSegment[];
  /**
   * `true` when this node's own name matches the search term, `false` when it
   * is only rendered to show the path down to a hit (WHP-12440). Only set on
   * search result nodes.
   */
  searchMatch?: boolean;

  constructor(data: IWHBomNodeDTO) {
    super(data);
    if (data) {
      // DTO
      Object.entries(data).forEach((property: any[]) => {
        this[property[0]] = property[1];
      });
    }
  }
}
